import dayjs from 'dayjs';
import deepEqual from 'deep-equal';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import { MDTypography } from '@components';
import { DATE_TIME_GROUP, DATE_GROUP, ColumnTypes, DATE_GROUP_WITHOUT_FORMAT } from '@constants/common';

import { useMaterialUIController, setPreventClosing } from '@context/MDContext';
import { FormControlLabel, Checkbox } from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  getXAxisLabelsExcluded,
  getDateFormat,
  getStartOfDateGroup,
  getEndOfDateGroup
} from '@utils/chart';
import { DateRange, Filters, VisualSettings } from '@DStypes/chart';
import { RootState } from '@store/store';

import useToast from './useToast';
import { MarkType } from '../pages/EditChart/components/Filter';
import { ADD_A_FILTER } from '@constants/eventNames';

interface IUseFilterProps {
  data: any[];
  config: any;
  isDate?: boolean;
  isPie?: boolean;
  isBar?: boolean;
  visualSettings: VisualSettings | null;
  setVisualSettings: React.Dispatch<
    React.SetStateAction<VisualSettings | null>
  >;
  isDateTime: boolean;
  dateGroup: DATE_GROUP | '';
  xAxisType: ColumnTypes;
  isKpi: boolean;
}

const useFilters = ({
  visualSettings,
  data,
  config,
  isDate,
  isPie,
  isBar,
  isKpi,
  setVisualSettings,
  isDateTime,
  dateGroup,
  xAxisType
}: IUseFilterProps) => {
  const [showFilter, setShowFilter] = useState(false);

  const [dateSliderDisabled, setDateSliderDisabled] = useState<boolean>(false);
  const [isDateGroupChanged, setDateGroupChanged] = useState<boolean>(false);

  const filters = visualSettings?.filters;

  const setFilters = (newFilters: Filters) => {
    setVisualSettings({
      ...visualSettings,
      filters: newFilters,
      filtersToggle: visualSettings?.filtersToggle,
      topOrBottomToggle: !!topOrBottom
    });
  };

  const xAxisField = isPie
    ? config?.series?.[0]?.dataFields?.category
    : isBar
    ? config?.yAxes?.[0]?.dataFields?.category
    : config?.xAxes?.[0]?.dataFields?.category;

  const yAxisField = isPie
    ? config?.series?.[0]?.dataFields?.value
    : isKpi
    ? config?.xAxes?.[0]?.dataFields?.category
    : isBar
    ? config?.yAxes?.[0]?.dataFields?.value
    : config?.yAxes?.[0]?.dataFields?.value;

  const isDateOrTime = isDate || isDateTime;

  useEffect(() => {
    if (!showFilter && isDateOrTime) {
      const xAxisLabelsExcluded = getXAxisLabelsExcluded({
        isDate: isDateOrTime,
        isDateGroupChanged,
        dateRange: visualSettings?.filters?.dateRange,
        data,
        xAxisField,
        setDateGroupChanged
      });

      if (xAxisLabelsExcluded)
        setVisualSettings((prev: any) => ({
          ...prev,
          filters: {
            ...prev?.filters,
            xAxisLabelsExcluded: xAxisLabelsExcluded
          }
        }));
    }
  }, [data?.[0]?.[xAxisField]]);

  const [controller, dispatch] = useMaterialUIController();
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );

  const { preventClosing } = controller;

  const date_group =
    currentChartData?.config.date_group === 'none'
      ? isDateTime
        ? DATE_TIME_GROUP.HOURS
        : DATE_GROUP.DAYS
      : currentChartData?.config.date_group;

  const [topOrBottom, setTopOrBottom] = useState('');
  const [sliderValue, setSliderValue] = useState(0);
  const [sliderDrag, setSliderDrag] = useState(false);
  const [sliderRangeValue, setSliderRangeValue] = useState<number[]>([
    0,
    data?.length - 1 || 0
  ]);

  const [dateRange, setDateRange] = useState<DateRange | null>();
  const [searchTextValue, setSearchTextValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [xAxisLabelsExcluded, setXAxisLabelsExcluded] = useState<string[]>(
    filters?.xAxisLabelsExcluded || []
  );

  useEffect(() => {
    if (!isDateOrTime) setSliderRangeValue([0, data?.length]);
    else setSliderRangeValue([0, data?.length - 1]);
  }, [data, isDateOrTime]);

  const getFilterItemContent = (
    item: string | number | Date | dayjs.Dayjs | null | undefined
  ): string => {
    if (isDateOrTime)
      // Check if date_group is part of DATE_GROUP_WITHOUT_FORMAT
      if (Object.values(DATE_GROUP_WITHOUT_FORMAT).includes(date_group))
        return `${item}`; // No formatting needed
      else
        return dayjs(item).format(getDateFormat(date_group)); // Apply formatting

    return `${item}`; // Return as string for non-date items
  };

  const copiedData = useMemo(
    () =>
      data?.map((item) => {
        if (item[yAxisField] && isDateOrTime) {
          return {
            ...item,
            formatted: getFilterItemContent(item[xAxisField])
          };
        } else if (
          item[yAxisField] &&
          !isDateOrTime &&
          xAxisField !== yAxisField
        ) {
          if (xAxisField === yAxisField) {
            const key = Object.keys(item)?.[1];

            return {
              ...item,
              formatted: item[xAxisField],
              yAxisField: item[key]
            };
          }

          return {
            ...item,
            formatted: item[xAxisField],
            yAxisField: item[yAxisField]
          };
        } else {
          const values = Object.values(item);
          const keys = Object.keys(item);

          const stringIndex = keys.findIndex((key) => key === xAxisField);

          if (stringIndex === -1) values.splice(0, 1);
          else values.splice(stringIndex, 1);

          const totalValue = values.reduce(
            (accumulator: any, currentValue: any) => accumulator + currentValue,
            0
          );

          return {
            ...item,
            formatted: getFilterItemContent(item[xAxisField]),
            yAxisField: totalValue
          };
        }
      }),
    [data]
  );

  const setDebouncedSearchText = useDebouncedCallback((text) => {
    setSearchText(text);
  }, 500);

  const filteredList = useMemo(
    () =>
      !searchText
        ? data
        : copiedData.filter((item: any) =>
            String(item['formatted'])
              .toLowerCase()
              .includes(searchText?.toLowerCase())
          ),
    [data, searchText]
  );

  const sortedArr = useMemo(
    () =>
      [...(copiedData || [])].sort((a, b) => {
        if (a['yAxisField'] > b['yAxisField']) return -1;

        if (a['yAxisField'] < b['yAxisField']) return 1;

        return 0;
      }),
    [copiedData]
  );

  const handleSliderDisable = (startInd: number, endInd: number) => {
    const rangeLength = endInd - startInd + 1;

    const selectedLabelsLength = data?.length - xAxisLabelsExcluded.length;

    if (rangeLength !== selectedLabelsLength) setDateSliderDisabled(true);
    else setDateSliderDisabled(false);
  };
  const { pushInfoToast } = useToast();

  const handleXaxisSelection = (value: boolean, xAxis: string) => {
    if (topOrBottom !== '')
      pushInfoToast(
        `${
          topOrBottom === 'top' ? 'Top' : 'Bottom'
        } ${sliderValue} filter removed.`
      );
    setSliderValue(data?.length);
    setTopOrBottom('');
    if (!value && !xAxisLabelsExcluded.includes(xAxis))
      setXAxisLabelsExcluded((prev) => [...prev, xAxis]);
    if (value && xAxisLabelsExcluded.includes(xAxis))
      setXAxisLabelsExcluded((prev) =>
        prev.filter((item: string) => item !== xAxis)
      );
  };

  const handleTopOrBottom = (value: boolean, field?: 'top' | 'bottom') => {
    if (!value || !field) {
      if (topOrBottom) {
        setTopOrBottom('');
        setXAxisLabelsExcluded([]);
      }
    } else {
      setTopOrBottom(field);
      handleSliderChange(Math.round(data.length / 4), field);
    }
  };

  const handleSliderChange = (value: number, topOrBottom: string) => {
    const selectedLabels = [...sortedArr];

    //Sorted equal y-axis values in alphabetical order for bottom scenario
    const selectedLabelsForBottom = [...selectedLabels].sort((a, b) => {
      if (a['yAxisField'] === b['yAxisField']) {
        if (a['text'] > b['text']) return 1;

        return -1;
      }

      return 0;
    });

    if (topOrBottom === 'top') selectedLabels.splice(0, value);
    else if (topOrBottom === 'bottom')
      selectedLabelsForBottom.splice(-value, value);

    setSliderValue(value);
    if (topOrBottom === 'top')
      setXAxisLabelsExcluded(selectedLabels.map((item) => item[xAxisField]));
    else
      setXAxisLabelsExcluded(
        selectedLabelsForBottom.map((item) => item[xAxisField])
      );
  };

  const handleSliderRangeChange = (
    [min, max]: number[],
    isXAxisSelection = true
  ) => {
    const selectedLabels: any[] = [...filteredList];
    const maxValue = Math.max(0, max);
    const minValue = Math.min(data?.length - 1, min);

    if (maxValue === minValue) selectedLabels.splice(minValue, 1);
    else selectedLabels.splice(minValue, maxValue - minValue + 1);

    setSliderRangeValue([minValue, maxValue]);

    if (isXAxisSelection)
      setXAxisLabelsExcluded(selectedLabels.map((item) => item[xAxisField]));
  };

  const handleSelectAll = (value: boolean) => {
    if (value) sendAmplitudeData(ADD_A_FILTER);
    if (topOrBottom) {
      setSliderValue(data?.length - 1);
      setTopOrBottom('');
      pushInfoToast(
        `${
          topOrBottom === 'top' ? 'Top' : 'Bottom'
        } ${sliderValue} filter removed.`
      );
    }

    if (isDateOrTime) setSliderRangeValue([0, data?.length - 1]);

    if (value) {
      setXAxisLabelsExcluded([]);
      setSearchTextValue('');
    } else {
      setXAxisLabelsExcluded(data?.map((list) => list[xAxisField]));
      setSearchTextValue('');
      setDateSliderDisabled(true);
    }
  };

  const handleSubmit = useDebouncedCallback(() => {
    let startInd = 0;
    let endInd = 0;

    if (isDateOrTime) {
      startInd = sliderRangeValue[0];
      endInd = sliderRangeValue[1];
    } else {
      endInd = sliderValue;
    }

    const newFilter: Filters = {
      type: topOrBottom || 'none',
      range: {
        startInd,
        endInd
      },
      dateRange: dateRange,
      xAxisLabelsExcluded
    };

    setFilters(newFilter);
    setPreventClosing(dispatch, false);
  }, 2000);

  // hook to handle submit on change in xAxis labels only after filters value is retrieved from api response
  useEffect(() => {
    if (
      visualSettings &&
      !deepEqual(xAxisLabelsExcluded, filters?.xAxisLabelsExcluded)
    ) {
      setPreventClosing(dispatch, true);
      handleSubmit();
    }
  }, [xAxisLabelsExcluded]);

  // hook to set boundaries of date-time slider based on filter selection
  useEffect(() => {
    // to avoid slider updates while drag in progress
    if (sliderDrag || !isDateOrTime) return;

    if (xAxisLabelsExcluded?.length === 0 && data?.length) {
      if (dateGroup && Object.values(DATE_GROUP_WITHOUT_FORMAT).includes(dateGroup)) {
        setDateRange({
          startingDate: data[0][xAxisField],
          endingDate: data[data.length - 1][xAxisField]
        });
      } else if (dateGroup === DATE_GROUP.WEEKS) {
        const startOfWeekDate = getStartOfDateGroup(
          dateGroup,
          dayjs(data[0][xAxisField])
        )
          .add(1, 'd')
          .format('YYYY-MM-DDTHH:mm:ss');

        const endOfWeekDate = getEndOfDateGroup(
          dateGroup,
          dayjs(data[data.length - 1][xAxisField])
        )
          .add(1, 'd')
          .format('YYYY-MM-DDTHH:mm:ss');

        setDateRange({
          startingDate: startOfWeekDate,
          endingDate: endOfWeekDate
        });
      } else if (dateGroup) {
        setDateRange({
          startingDate: getStartOfDateGroup(
            dateGroup,
            dayjs(data[0][xAxisField])
          ).format('YYYY-MM-DDTHH:mm:ss'),
          endingDate: getEndOfDateGroup(
            dateGroup,
            dayjs(data[data.length - 1][xAxisField])
          ).format('YYYY-MM-DDTHH:mm:ss')
        });
      }

      handleSliderRangeChange([0, data?.length - 1], false);
    } else {
      const xAxisLabels = data?.map((item) => item[xAxisField]);
      const xAxisLabelsIncluded = xAxisLabels?.filter(
        (field) => !xAxisLabelsExcluded.includes(field)
      );

      if (xAxisLabelsIncluded?.length) {
        const dateTimes = xAxisLabelsIncluded.map((datetime) =>
          dayjs(datetime)
        );

        const startDate = dayjs.min(dateTimes).format('YYYY-MM-DDTHH:mm:ss');
        const endDate = dayjs.max(dateTimes).format('YYYY-MM-DDTHH:mm:ss');

        const startInd = xAxisLabels?.indexOf(startDate);
        const endInd = xAxisLabels?.indexOf(endDate);

        if (startDate === 'Invalid Date')
          handleSliderRangeChange([0, data?.length - 1], false);
        else handleSliderRangeChange([startInd, endInd], false);

        if (dateGroup === DATE_GROUP.WEEKS) {
          const startOfWeekDate = getStartOfDateGroup(
            dateGroup,
            dayjs(startDate)
          )
            .add(1, 'd')
            .format('YYYY-MM-DDTHH:mm:ss');
          const endOfWeekDate = getEndOfDateGroup(dateGroup, dayjs(endDate))
            .add(1, 'd')
            .format('YYYY-MM-DDTHH:mm:ss');

          setDateRange({
            startingDate: startOfWeekDate,
            endingDate: endOfWeekDate
          });
        } else {
          setDateRange({
            startingDate: getStartOfDateGroup(
              dateGroup,
              dayjs(startDate)
            ).format('YYYY-MM-DDTHH:mm:ss'),
            endingDate: getEndOfDateGroup(dateGroup, dayjs(endDate)).format(
              'YYYY-MM-DDTHH:mm:ss'
            )
          });
        }
      }
    }
  }, [xAxisLabelsExcluded, sliderDrag]);

  // hook to update search text in filter
  useEffect(() => {
    setDebouncedSearchText(searchTextValue);
  }, [searchTextValue]);

  // hook to update non date range slider on filter change
  useEffect(() => {
    if (filters && !isDateOrTime && data?.length)
      if (filters.type !== 'none') {
        setTopOrBottom(filters.type);
        handleSliderChange(
          filters.range?.endInd > data.length
            ? data.length
            : filters.range?.endInd,
          filters.type
        );
      } else {
        setXAxisLabelsExcluded(filters.xAxisLabelsExcluded);
        setTopOrBottom('');
      }

    if (filters && isDateOrTime && data?.length)
      setXAxisLabelsExcluded(filters.xAxisLabelsExcluded);
  }, [filters, data]);

  // hook to disable date range slider
  useEffect(() => {
    if (isDateOrTime)
      handleSliderDisable(sliderRangeValue[0], sliderRangeValue[1]);
  }, [sliderRangeValue, filters]);

  // hook to handle date group change
  useEffect(() => {
    if (isDateOrTime) {
      const xAxisLabelsExcluded = getXAxisLabelsExcluded({
        isDate,
        isDateGroupChanged,
        dateRange: filters?.dateRange,
        data,
        xAxisField,
        setDateGroupChanged
      });

      if (xAxisLabelsExcluded) setXAxisLabelsExcluded(xAxisLabelsExcluded);
      if (isDateOrTime && isDateGroupChanged) setDateSliderDisabled(false);
    }
  }, [data]);

  useEffect(() => () => setPreventClosing(dispatch, false), []);

  const list = useMemo(
    () =>
      filteredList?.map((item: any, index: number) => {
        const isNumberItem = !isNaN(
          Number(getFilterItemContent(item[xAxisField]))
        );

        const listItem = isNumberItem
          ? Number(getFilterItemContent(item[xAxisField])).toLocaleString()
          : getFilterItemContent(item[xAxisField]);

        return (
          <FormControlLabel
            key={index}
            checked={!xAxisLabelsExcluded?.includes(item[xAxisField])}
            onChange={(e, value) => {
              handleXaxisSelection(value, item[xAxisField]);
              if (value) sendAmplitudeData(ADD_A_FILTER);
            }}
            control={<Checkbox sx={{ marginLeft: '11px' }} />}
            label={
              <MDTypography variant='xs' color='info'>
                {listItem}
              </MDTypography>
            }
          />
        );
      }),
    [filteredList, xAxisLabelsExcluded, filters, xAxisField]
  );

  const getDateString = (index: number) =>
    searchText.length !== 0
      ? getFilterItemContent(data?.[index]?.[xAxisField])
      : getFilterItemContent(filteredList?.[index]?.[xAxisField]);

  const renderSliderLabel = (value: number) =>
    isDateOrTime ? getDateString(value) : `${value}`;

  const sliderMarks: MarkType[] = useMemo(() => {
    const min = 0;
    const max = isDateOrTime ? data?.length - 1 : data?.length;

    return [
      {
        value: min,
        label: isDateOrTime ? getDateString(min) : '0'
      },
      {
        value: max,
        label: isDateOrTime ? getDateString(max) : `${max}`
      }
    ];
  }, [data]);

  const getSliderToolTip = (): string => {
    if (xAxisType !== ColumnTypes.DATE && xAxisType !== ColumnTypes.DATETIME)
      return 'These values cannot be filtered by range. Please use the below section.';

    if (isDateOrTime && dateSliderDisabled)
      return 'Move the scale to show the date range (Ex: 1 April 22 to 30 June 22)';

    return ``;
  };

  return {
    getSliderToolTip,
    renderSliderLabel,
    list,
    handleTopOrBottom,
    handleSelectAll,
    setSliderDrag,
    preventClosing,
    topOrBottom,
    sliderValue,
    handleSliderChange,
    sliderMarks,
    setShowFilter,
    showFilter,
    xAxisField,
    dateSliderDisabled,
    getDateString,
    sliderRangeValue,
    handleSliderRangeChange,
    setDateSliderDisabled,
    xAxisLabelsExcluded,
    searchTextValue,
    setSearchTextValue,
    autoGroupNumbers: visualSettings?.autoGroupNumbers
  };
};

export default useFilters;
